import { createTheme } from '@mui/material/styles';
import { PHI } from 'utils/variables';
import { darken, lighten, rgba } from 'polished';

const { shadows } = createTheme();

export const muiTheme = ({ colors, mode }) =>
  createTheme({
    direction: 'rtl',
    spacing: [0, 4, 8, 16, 32, 64],
    typography: {
      fontFamily: ['persian', 'roboto'].join(','),
    },
    shape: {
      borderRadius: 8,
    },
    shadows: [
      ...shadows,
      `0 ${PHI / 20}rem ${PHI / 12}rem ${rgba(colors.blackColor, 0.05)}`,
    ],
    palette: {
      mode,
      primary: {
        main: colors.tertiaryColor,
        light: colors.tertiaryColorLight,
        dark: colors.tertiaryColorDark,
        contrastText: '#fff',
      },
      secondary: {
        main: colors.accentColor,
        light: colors.accentColorLight,
        dark: colors.accentColorDark,
        contrastText: '#fff',
      },
      error: {
        main: colors.redColor,
        light: colors.redColorLight,
        dark: colors.redColorDark,
      },
      warning: {
        main: colors.orangeColor,
        light: colors.orangeColorLight,
        dark: colors.orangeColorDark,
      },
      success: {
        main: colors.greenColor,
        light: colors.greenColorLight,
        dark: colors.greenColorDark,
      },
      info: {
        main: colors.blueColor,
        light: colors.blueColorLight,
        dark: colors.blueColorDark,
      },
      background: {
        default: lighten(0.1, colors.primaryColor),
        paper: lighten(0.1, colors.primaryColor),
      },
      divider: darken(0.08, colors.primaryColor),

      // grey: {
      //   50: '#F8F9FD',
      //   100: '#E0E1E7',
      //   200: '#C7C9D1',
      //   300: '#AEB1BB',
      //   400: '#9598A4',
      //   500: '#636778',
      //   600: '#4A4F62',
      //   700: '#3E4357',
      //   800: '#383D51',
      //   900: '#31364B',
      //   A100: '#F8F9FD',
      //   A200: '#E0E1E7',
      //   A400: '#636778',
      //   A700: '#383D51',
      // },
    },
    components: {
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: '40px',
          },
          indicator: {
            display: 'none',
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            paddingRight: '8px',
            paddingLeft: '8px',
            minHeight: '40px',
            fontSize: '0.9rem',
            '&.Mui-selected': {
              backgroundColor: colors.primaryColor,
              fontWeight: 'bold',
              borderRadius: '8px',
            },
          },
        },
      },

      MuiPopover: {
        styleOverrides: {
          paper: {
            boxShadow: `0 ${PHI / 20}rem ${PHI / 12}rem ${rgba(
              colors.blackColor,
              0.05,
            )}`,
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: {
            maxWidth: 500,
            margin: '0 auto',
            borderRadius: '10px 10px 0 0',
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          sizeMedium: {
            padding: '10px 16px',
            fontSize: '1rem',
          },
          colorInherit: {
            backgroundColor: darken(0.05, colors.primaryColor),
            '&:hover': {
              backgroundColor: lighten(0.05, colors.primaryColor),
            },
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            gap: '12px',
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderColor: 'transparent',
            boxShadow: `0 ${PHI / 20}rem ${PHI / 12}rem ${rgba(
              colors.blackColor,
              0.05,
            )}`,
            backgroundColor: lighten(0.1, colors.primaryColor),
            borderRadius: '8px !important',
            margin: '0 !important',

            '&.MuiToggleButton-primary': {
              backgroundColor: lighten(0.02, colors.primaryColor),

              '&.Mui-selected': {
                backgroundColor: lighten(0.1, colors.primaryColor),
                borderColor: colors.tertiaryColor,
                border: 'solid 1px !important',
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: lighten(0.1, colors.primaryColor),
          },
          input: {
            padding: 12,
          },
          notchedOutline: {
            borderColor: darken(0.08, colors.primaryColor),
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {},
          select: {
            backgroundColor: lighten(0.1, colors.primaryColor),
            padding: 12,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          option: {
            borderColor: darken(0.08, colors.primaryColor),
            borderBottomWidth: 1,
            borderBottomStyle: 'solid',
          },
          inputRoot: {
            padding: '4.5px 6px',
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            height: 1,
          },
          thumb: {
            backgroundColor: lighten(0.1, colors.primaryColor),
            border: `2px solid ${colors.tertiaryColor}`,
          },
          mark: {
            width: 2,
            height: 8,
            backgroundColor: darken(0.1, colors.primaryColor),
          },
          markActive: {
            backgroundColor: colors.tertiaryColor,
          },
          rail: {
            backgroundColor: darken(0.1, colors.primaryColor),
            opacity: 1,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            borderRadius: '1000px',
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&:not(.Mui-focused):not(.MuiFormLabel-filled)': {
              top: '-4px',
            },
            // transform: 'translate(-14px, 12px) scale(1)',
          },
        },
      },
    },
  });
